import { useState } from "react";
import { ArrowLeft, ArrowRight, MessageCircle, Mic, Phone, QrCode, Puzzle, Globe, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { PublicGeniePersona } from "@/types/discovery-catalog";
import type { BaseCapability } from "@/types/genie";
import { PersonaBrand } from "@/data/genies/persona-brands";

interface DemoHeroPanelProps {
  persona: PublicGeniePersona;
  brand: PersonaBrand | undefined;
  primaryColor: string;
  logoPath: string | null;
  capability: BaseCapability | undefined;
  isMobile: boolean;
  onBack: () => void;
  onCloneClick: () => void;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

export const DemoHeroPanel = ({
  persona,
  brand,
  primaryColor,
  logoPath,
  capability,
  isMobile,
  onBack,
  onCloneClick,
}: DemoHeroPanelProps) => {
  const [logoError, setLogoError] = useState(false);
  const [imgError, setImgError] = useState(false);
  const secondaryColor = brand?.secondaryColor || primaryColor;
  const suggestedReplies = persona.config.suggestedReplies || [];

  // Resolve industry background image — reuse solution page hero images
  const industryId = persona.industryId;
  const heroImagePath = `/solutions-pages/${industryId}/hero.webp`;
  const fallbackGradient = `linear-gradient(160deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;

  // Platform highlights — what makes HelpGenie special
  const highlights = [
    { icon: Mic, label: "Voice or Text Chat", desc: "Talk or type — your customers choose" },
    { icon: Phone, label: "Distribute via Phone", desc: "Connect to any phone number instantly" },
    { icon: QrCode, label: "QR Code Ready", desc: "Scan-to-talk from anywhere" },
    { icon: Puzzle, label: "Custom Integrations", desc: "Connect to your existing tools" },
    { icon: Globe, label: "Embed Anywhere", desc: "Website widget, app, or standalone" },
    { icon: Sparkles, label: "Fully Customizable", desc: "Voice, personality, and responses" },
  ];

  // Mobile: slim top bar only — conversation area has its own greeting/avatar
  if (isMobile) {
    return (
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 14px",
          background: "white",
          borderBottom: "1px solid #E5E7EB",
          zIndex: 50,
        }}
      >
        {/* Back */}
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid #E5E7EB",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6B7280",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={14} strokeWidth={2} />
        </button>

        {/* Logo */}
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            background: `${primaryColor}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {logoPath && !logoError ? (
            <img
              src={logoPath}
              alt=""
              style={{ width: "65%", height: "65%", objectFit: "contain" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div style={{ width: "50%", height: "50%", borderRadius: "50%", background: primaryColor }} />
          )}
        </div>

        {/* Name */}
        <span
          style={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: "#1F2937",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {persona.name}
        </span>

        {/* CTA */}
        <Button
          size="sm"
          onClick={onCloneClick}
          style={{
            background: primaryColor,
            color: "white",
            fontWeight: 600,
            borderRadius: "16px",
            padding: "6px 14px",
            fontSize: "0.75rem",
            border: "none",
            flexShrink: 0,
          }}
        >
          Use Genie
        </Button>
      </div>
    );
  }

  // Desktop: full hero panel
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: fallbackGradient,
      }}
    >
      {/* Background image with dark overlay */}
      {!imgError && (
        <motion.img
          src={heroImagePath}
          alt=""
          initial={{ scale: 1.05 }}
          animate={{ scale: 1.1 }}
          transition={{ duration: 20, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          onError={() => setImgError(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />
      )}
      {/* Dark overlay for text readability */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: imgError
            ? "transparent"
            : `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.8) 100%)`,
          zIndex: 1,
        }}
      />
      {/* Gradient tint overlay for brand color bleed */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(160deg, ${primaryColor}40 0%, transparent 60%)`,
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      {/* Back button */}
      <div style={{ padding: "16px 20px", flexShrink: 0, position: "relative", zIndex: 10 }}>
        <button
          onClick={onBack}
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            cursor: "pointer",
            backdropFilter: "blur(8px)",
            transition: "all 0.2s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          }}
        >
          <ArrowLeft size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Main content — vertically centered */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          flex: 1,
          position: "relative",
          zIndex: 10,
          padding: "0 40px 24px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        {/* Brand logo */}
        <motion.div variants={itemVariants}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
            }}
          >
            {logoPath && !logoError ? (
              <img
                src={logoPath}
                alt={brand?.brandName || persona.name}
                style={{
                  width: "60%",
                  height: "60%",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <div
                style={{
                  width: "45%",
                  height: "45%",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.5)",
                }}
              />
            )}
          </div>
        </motion.div>

        {/* Persona name */}
        <motion.h1
          variants={itemVariants}
          style={{
            fontSize: "2.5rem",
            fontWeight: 800,
            color: "#ffffff",
            margin: 0,
            lineHeight: 1.1,
            textShadow: "0 2px 20px rgba(0, 0, 0, 0.3)",
            letterSpacing: "-0.03em",
          }}
        >
          {persona.name}
        </motion.h1>

        {/* Description */}
        <motion.p
          variants={itemVariants}
          style={{
            fontSize: "1.1rem",
            color: "rgba(255, 255, 255, 0.8)",
            margin: 0,
            lineHeight: 1.5,
            maxWidth: "420px",
            textShadow: "0 1px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          {persona.description}
        </motion.p>

        {/* Role + Capability pills */}
        <motion.div
          variants={itemVariants}
          style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
        >
          <span
            style={{
              padding: "6px 16px",
              borderRadius: "20px",
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "white",
              background: "rgba(255, 255, 255, 0.15)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
            }}
          >
            {persona.role}
          </span>
          {capability && (
            <span
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "white",
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              {capability.name}
            </span>
          )}
        </motion.div>

        {/* Suggested replies */}
        {suggestedReplies.length > 0 && (
          <motion.div variants={itemVariants} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <span
              style={{
                fontSize: "0.75rem",
                color: "rgba(255, 255, 255, 0.45)",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "5px",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <MessageCircle size={12} />
              Try asking
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {suggestedReplies.map((reply) => (
                <span
                  key={reply}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "20px",
                    fontSize: "0.84rem",
                    fontWeight: 500,
                    color: "rgba(255, 255, 255, 0.9)",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.18)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  "{reply}"
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Bottom section: benefits + CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.45 }}
        style={{
          padding: "20px 40px 32px",
          flexShrink: 0,
          position: "relative",
          zIndex: 10,
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {/* Section label */}
        <p
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "rgba(255, 255, 255, 0.4)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            margin: "0 0 10px 0",
          }}
        >
          What's included
        </p>

        {/* Platform highlights — 2 column grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          {highlights.map((h) => {
            const Icon = h.icon;
            return (
              <div
                key={h.label}
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.06)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    background: `${primaryColor}30`,
                    border: `1px solid ${primaryColor}50`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={14} style={{ color: "rgba(255,255,255,0.85)" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "rgba(255,255,255,0.9)", margin: 0, lineHeight: 1.3 }}>
                    {h.label}
                  </p>
                  <p style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)", margin: "2px 0 0", lineHeight: 1.3 }}>
                    {h.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust line */}
        <p
          style={{
            fontSize: "0.75rem",
            color: "rgba(255, 255, 255, 0.4)",
            margin: "0 0 14px 0",
          }}
        >
          No credit card required · Ready in 60 seconds
        </p>

        {/* CTA */}
        <Button
          size="default"
          onClick={onCloneClick}
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            color: primaryColor,
            fontWeight: 700,
            borderRadius: "24px",
            padding: "12px 28px",
            fontSize: "0.92rem",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
            transition: "all 0.2s ease",
            border: "none",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 6px 28px rgba(0, 0, 0, 0.3)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.2)";
          }}
        >
          Use This Genie — Free
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </motion.div>
    </div>
  );
};
